import * as React from "react";
import {useUserDetails} from "./UserDetailsContext";
import {DATA_STATE} from "./UsersDashboardContext";
import {RestDataLoader} from "../../dashboard/widgets/unilever/RestDataLoader";
import {commercialHierarchyTreeToList} from "../../processes/user_creation/UserCreation";
import {useUserActivities} from "./UserActivitiesContext";
import {HierarchiesSection} from "../HierarchyVisibilityUtils";

export type UserHierarchy = {
    hierarchyTypeGuid: string;
    hierarchyNodesGuids: any[];
    empty: boolean;
}

export type UserHierarchyForRoles = {
    hierarchyTypeGuid: string;
    hierarchyNodesGuids: string[];
}

export enum HierarchyError {
    HIERARCHY_NOT_FOUND = "HIERARCHY_NOT_FOUND",
    INVALID_ROLES_FOR_HIERARCHY = "INVALID_ROLES_FOR_HIERARCHY"
}

export type HierarchyDataState = DATA_STATE | HierarchyError

export type UserVisibility = [
    UserHierarchy,
    HierarchyDataState,
    (newUserHierarchy: HierarchiesSection) => void,
    () => void,
    string
]

const UserVisibilityHierarchiesContext = React.createContext<UserVisibility>({} as any)
export const UserVisibilityHierarchiesProvider: React.FC = ({children}) => {
    const [, , updateActivities] = useUserActivities();
    const [currentUser, , reloadCurrentUser] = useUserDetails();
    const [state, setState] = React.useState<HierarchyDataState>(DATA_STATE.isLoading);
    const [errorMessage, setErrorMessage] = React.useState<string>();
    const [isFirstUpdate, setIsFirstUpdate] = React.useState(true);

    const [currentUserHierarchies, setCurrentUserHierarchies] = React.useState<UserHierarchy>();

    const setNewUserVisibilityHierarchies = (newUserHierarchy: HierarchiesSection) => {
        setState(DATA_STATE.isLoading);
        setUserVisibilityHierarchies(currentUser.guid, newUserHierarchy)
            .then((newUserVisibility) => {
                setCurrentUserHierarchies(newUserVisibility);
                setState(DATA_STATE.loaded);
                reloadCurrentUser();
            })
            .catch((err) => {
                if (err.errorType === "INVALID_ROLES_FOR_COMPANY") {
                    setState(HierarchyError.INVALID_ROLES_FOR_HIERARCHY);
                    setErrorMessage(err.message)
                } else {
                    setState(DATA_STATE.error)
                }
                console.error(err);
            });
    }

    const updateUserVisibilityHierarchies = () => {
        setState(DATA_STATE.isLoading);
        getUserVisibilityHierarchies(
            currentUser.guid)
            .then((userVisibility) => {
                setCurrentUserHierarchies(userVisibility);
                setState(DATA_STATE.loaded);
                if (isFirstUpdate) {
                    setIsFirstUpdate(false);
                }
            })
            .catch((error) => {
                if (error.errorType === "HTTP_NOT_FOUND") {
                    setState(HierarchyError.HIERARCHY_NOT_FOUND);
                } else {
                    console.error(error);
                    setState(DATA_STATE.error)
                }
                console.error(error);
            })
    }

    React.useEffect(() => {
        if (currentUser && isFirstUpdate) {
            updateUserVisibilityHierarchies();
        }
    }, [currentUser])

    return <UserVisibilityHierarchiesContext.Provider value={[currentUserHierarchies, state, setNewUserVisibilityHierarchies, updateUserVisibilityHierarchies, errorMessage]}>
        {children}
    </UserVisibilityHierarchiesContext.Provider>
}

export function useUserHierarchyVisibility() {
    return React.useContext(UserVisibilityHierarchiesContext);
}


function getUserVisibilityHierarchies(guid: string) {
    return new Promise<UserHierarchy>((res, rej) => {
        RestDataLoader.loadData(
            "users/" + guid + "/hierarchies",
            {},
            (data: UserHierarchy) => {
                return res(Object.keys(data).length === 0 ? undefined : data);
            },
            rej,
        );
    })
}

function setUserVisibilityHierarchies(guid: string, hierarchiesSelected: HierarchiesSection) {
    let newUserHierarchy: UserHierarchy;

    if (Array.isArray(hierarchiesSelected.selectedHierarchies)) {
        newUserHierarchy = {
            hierarchyTypeGuid: hierarchiesSelected.selectedHierarchies[0],
            hierarchyNodesGuids: hierarchiesSelected.selectedCommercialHierarchies.length === 0
                ? [hierarchiesSelected.selectedHierarchies.pop()]
                : commercialHierarchyTreeToList(hierarchiesSelected.selectedCommercialHierarchies),
            empty: false
        }
    }

    return new Promise<UserHierarchy>((res, rej) => {
        RestDataLoader.putJSON(
            "users/" + guid + "/hierarchies",
            newUserHierarchy ? newUserHierarchy : {},
            (result: UserHierarchy) => {
                return res(result)
            },
            rej,
        );
    })
}